import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";

export function useConversationFrameAgentRouting({
  agentKey,
  agentName,
  ctx,
  frameId,
  projectAgentId,
}: {
  ctx: CursorInputContextValue | null;
  frameId: string;
  projectAgentId?: string;
  agentKey?: string;
  agentName?: string;
}) {
  const frameAgent = useMemo(() => {
    if (!ctx) return null;
    return (
      (projectAgentId
        ? ctx.inputAgents.find((candidate) => candidate.id === projectAgentId)
        : null) ??
      (agentKey
        ? ctx.inputAgents.find(
            (candidate) => candidate.name.toLowerCase() === agentKey,
          )
        : null) ??
      (agentName
        ? ctx.inputAgents.find(
            (candidate) =>
              candidate.name.toLowerCase() === agentName.toLowerCase(),
          )
        : null) ??
      null
    );
  }, [agentKey, agentName, ctx, projectAgentId]);

  const [frameSelectedAgentIds, setFrameSelectedAgentIds] = useState<
    Set<string>
  >(() => new Set());
  const lastFrameAgentSelectionKeyRef = useRef<string | null>(null);
  const frameAgentSelectionKey =
    frameAgent?.id ??
    (projectAgentId ? `project:${projectAgentId}` : null) ??
    (agentKey ? `key:${agentKey}` : null) ??
    (agentName ? `name:${agentName.toLowerCase()}` : null) ??
    frameId;

  const resetFrameSelectedAgent = useCallback(() => {
    setFrameSelectedAgentIds(
      frameAgent?.id ? new Set([frameAgent.id]) : new Set(),
    );
  }, [frameAgent?.id]);

  useEffect(() => {
    if (lastFrameAgentSelectionKeyRef.current === frameAgentSelectionKey) {
      return;
    }
    lastFrameAgentSelectionKeyRef.current = frameAgentSelectionKey;
    resetFrameSelectedAgent();
  }, [frameAgentSelectionKey, resetFrameSelectedAgent]);

  const selectedInputAgent = useMemo(() => {
    const selectedId = frameSelectedAgentIds.values().next().value;
    return (
      (selectedId
        ? ctx?.inputAgents.find((candidate) => candidate.id === selectedId)
        : null) ??
      frameAgent ??
      null
    );
  }, [ctx?.inputAgents, frameAgent, frameSelectedAgentIds]);

  const focusedAgent = useMemo(() => {
    if (!ctx) return null;
    return selectedInputAgent
      ? ctx.getFocusedAgentInfo(selectedInputAgent.id)
      : ctx.focusedAgentInfo;
  }, [ctx, selectedInputAgent]);

  return {
    focusedAgent,
    frameAgent,
    frameSelectedAgentIds,
    resetFrameSelectedAgent,
    setFrameSelectedAgentIds,
  };
}
